import * as React from "react"
import { Box, Flex } from "@chakra-ui/react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { ChatBubble } from "../../servicesPanel/chat/_chatBubble"
import {
  bottomMostUnreadAtom,
  chatListSelector,
  chatScrollStateAtom,
  topMostUnreadAtom
} from "../../../recoil/atoms/chats"
import { brandingSelector, roomSelector } from "../../../recoil/atoms/room"
// import { chatIsReadAtom } from "../../../recoil/atoms/chats"
import { VscArrowDown } from "react-icons/vsc"
import { Icon } from "../../common/icons"
import { badgeHistoryListSelector } from "../../../recoil/atoms/badgeHistory"
import { FadeChat } from "../../servicesPanel/chat/_fadeChat"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import ChatVirtualizedList from "../../servicesPanel/chat/ChatVirtualizedList"
import useLocalUserHasPermissions from "../../../hooks/useLocalUserHasPermissions"
import { useSessionStorage } from "../../../hooks/useSessionStorage"
import layoutStates from "../../../recoil/atoms/layout"
import { ChatPolls } from "../../servicesPanel/polls/chat-polls"
import styled from "@emotion/styled"
import { waitForElm } from "../../../util/waitForElm"
import { useWatchParty } from "../../../hooks/useWatchParty"
import useWindowSize from "../../../hooks/useWindowSize"
import { VIDEO_OVERLAY_Z_INDEX } from "../../../constants"
import { scrollToBottomContainer } from "../../../util/scrollTo"
import { ElementId, UserType } from "../../../constants/enums"
import DaySplitter from "../../servicesPanel/chat/_daySplitter"
import useConfig from "src/hooks/useConfig"
import { withErrorBoundary } from "react-error-boundary"
import ComponentFallback from "src/components/common/errors/componentFallback"
import { CloseTabsIcon } from "./full-screen-mobile-interface/close-tab-icon"

type PostionScrollType = "top" | "bottom" | "middle" | "noHasScrollbar"

interface ChatStreamProps {
  isFullScreen?: boolean
  scrollToBottom: () => void
  isMobile?: boolean
  isTablet?: boolean
  isFullScreenChat?: boolean
  mobileFullScreenChatPosition?: string
  handleTouchEnd?: any
  handleTouchStart?: any
  paddingLeft?: `${string}px` | `${string}rem`,
  paddingBottom?: `${string}px` | `${string}rem`
}

interface StyledProps {
  positionscroll: PostionScrollType
}

const Container = styled(Box)<StyledProps>`
  -webkit-align-content: end;
  align-content: end;
  --scrollbar-width-product-description: 1px;
  --mask-height-product-description: 30px;
  overflow-y: auto;
  --mask-size-content-product-description: calc(
      100% - var(--scrollbar-width-product-description)
    )
    100%;
  --mask-image-scrollbar-product-description: linear-gradient(black, black);
  --mask-size-scrollbar-product-description: var(
      --scrollbar-width-product-description
    )
    100%;
  mask-image: ${({ positionscroll }) =>
    positionscroll === "noHasScrollbar"
      ? ""
      : `linear-gradient(
    to ${positionscroll === "top" ? "top" : "bottom"},
    transparent,
    black var(--mask-height-product-description),
    black calc(100% - ${
      positionscroll === "middle"
        ? "var(--mask-height-product-description)"
        : "0px"
    }),
    transparent
  ),
  var(--mask-image-scrollbar-product-description)`};
  mask-size: var(--mask-size-content-product-description),
    var(--mask-size-scrollbar-product-description);
  mask-position: 0 0, 100% 0;
  mask-repeat: no-repeat, no-repeat;
`

const _MobileChatStream = withErrorBoundary(
  ({
    scrollToBottom,
    isFullScreen,
    isMobile,
    isTablet,
    isFullScreenChat,
    mobileFullScreenChatPosition,
    handleTouchEnd,
    handleTouchStart,
    paddingLeft,
    paddingBottom = "0px"
  }: // handleScrollTarget,
  // handleScrollToBottomChat
  ChatStreamProps) => {
    const config = useConfig()
    const { screenSize } = useWindowSize()
    const allChatMessages = useRecoilValue(chatListSelector)
    const roomStyles = useRecoilValue(brandingSelector)

    const room = useRecoilValue(roomSelector)
    const { canSendChat } = useLocalUserHasPermissions()
    const bottomMostUnread = useRecoilValue(bottomMostUnreadAtom)
    const topMostUnread = useRecoilValue(topMostUnreadAtom)
    const setChatScrollState = useSetRecoilState(chatScrollStateAtom)
    const { isLandscape, isTransparent } = useRecoilValue(
      viewDependingLayoutAtom
    )

    const [positionScroll, setPositionScroll] =
      React.useState<PostionScrollType>("noHasScrollbar")

    const { isWatchParty } = useWatchParty()
    /* React.useEffect(() => {
    scrollToBottom()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) */

    const styleschat = React.useMemo(() => {
      return roomStyles
    }, [roomStyles])

    const chatApprovalRequired = React.useMemo(() => {
      return room.chatApprovalRequired
    }, [room.chatApprovalRequired])

    const handleGoUp = React.useCallback(() => {
      setChatScrollState("UP")
    }, [setChatScrollState])

    const handleGoDown = React.useCallback(() => {
      setChatScrollState("DOWN")
    }, [setChatScrollState])

    const { setSessionStorage } = useSessionStorage("reactions")

    const renderUserChatMessageAtRight = React.useCallback(
      message =>
        config.mobileChatShowUserMessagesRight &&
        message.author.type !== UserType.AVATAR,
      [config.mobileChatShowUserMessagesRight]
    )

    const renderChatMessage = React.useCallback(
      (message, index) => (
        <ChatBubble
          key={`key-${message.id}`}
          isFirstMessage={index === 0}
          isLastMessage={allChatMessages.length - 1 === index}
          messageId={message.id}
          isMobile={true}
          handleTouchStart={handleTouchStart}
          handleTouchEnd={handleTouchEnd}
          styleschat={styleschat}
          canSendChat={canSendChat}
          isTransparent={isTransparent}
          chatApprovalRequired={chatApprovalRequired}
          setSessionStorage={setSessionStorage}
          alignChatRight={renderUserChatMessageAtRight(message)}
        />
      ),
      [
        allChatMessages.length,
        handleTouchStart,
        handleTouchEnd,
        styleschat,
        canSendChat,
        isTransparent,
        chatApprovalRequired,
        setSessionStorage,
        renderUserChatMessageAtRight
      ]
    )
    const setHideFSScrollControl = useSetRecoilState(
      layoutStates.hideFSScrollControlChat
    )
    const mobileChatContainerRef = React.useRef<HTMLDivElement>(null)
    const targetTabIndex = useRecoilValue<number>(
      layoutStates.historyTabEnabledMobile
    )

    const handleDisableControls = async () => {
      const upSvgElement = (await waitForElm("fs-up-icon")) as HTMLElement
      const downSvgElement = (await waitForElm("fs-down-icon")) as HTMLElement
      if (downSvgElement) {
        if (
          mobileChatContainerRef?.current?.scrollTop >=
          mobileChatContainerRef?.current?.scrollHeight -
            mobileChatContainerRef?.current?.clientHeight -
            10
        ) {
          downSvgElement.style.opacity = "0.5"
        } else {
          downSvgElement.style.opacity = "1"
        }
      }

      if (upSvgElement) {
        if (mobileChatContainerRef?.current?.scrollTop < 10) {
          upSvgElement.style.opacity = "0.5"
        } else {
          upSvgElement.style.opacity = "1"
        }
      }
    }

    React.useEffect(() => {
      const mobileChatContainer = mobileChatContainerRef.current
      let timerId = null
      const observer = new ResizeObserver(entries => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (let entry of entries) {
          const firstMessage = document.getElementById(
            `chat-${allChatMessages[0]?.id}`
          ) as HTMLDivElement
          const lastMessage = document.getElementById(
            `chat-${allChatMessages[allChatMessages.length - 1]?.id}`
          ) as HTMLDivElement

          if (
            allChatMessages.length < 2 ||
            (firstMessage?.getBoundingClientRect().top >=
              mobileChatContainer?.getBoundingClientRect().top &&
              lastMessage?.getBoundingClientRect().bottom <=
                mobileChatContainer?.getBoundingClientRect().bottom)
          ) {
            setPositionScroll("noHasScrollbar")
            setHideFSScrollControl(true)
          } else {
            setHideFSScrollControl(false)
            if (mobileChatContainerRef?.current?.scrollTop < 10) {
              setPositionScroll("top")
            } else if (
              mobileChatContainerRef?.current?.scrollTop >=
              mobileChatContainerRef?.current?.scrollHeight -
                mobileChatContainerRef?.current?.clientHeight -
                10
            ) {
              setPositionScroll("bottom")
            } else {
              setPositionScroll("middle")
            }
          }
        }
        handleDisableControls()

        // Scroll to the bottom if the distance to the bottom is less when the user drags.
        const bottomDistance =
          mobileChatContainerRef?.current?.scrollHeight -
          (mobileChatContainerRef?.current?.scrollTop +
            mobileChatContainerRef?.current?.clientHeight)
        const timeDelay = 300
        if (bottomDistance < 100) {
          clearTimeout(timerId)
          timerId = setTimeout(() => {
            scrollToBottomContainer(ElementId.MOBILE_CHAT_CONTAINER, 0)
          }, timeDelay)
        }
      })

      if (mobileChatContainer) {
        observer.observe(mobileChatContainer)
      }

      return () => {
        if (mobileChatContainer) {
          observer.unobserve(mobileChatContainer)
        }
        clearTimeout(timerId)
      }
    }, [
      mobileChatContainerRef,
      allChatMessages,
      targetTabIndex,
      setHideFSScrollControl
    ])

    React.useEffect(() => {
      handleDisableControls()
    }, [isWatchParty])

    const handleScroll = () => {
      if (
        mobileChatContainerRef?.current?.scrollHeight ===
        mobileChatContainerRef?.current?.clientHeight
      ) {
        // hide the shadow effect if there is no scroll present
        setPositionScroll("noHasScrollbar")
        setHideFSScrollControl(true)
      } else {
        if (mobileChatContainerRef?.current?.scrollTop < 10) {
          setPositionScroll("top")
        } else if (
          mobileChatContainerRef?.current?.scrollTop >=
          mobileChatContainerRef?.current?.scrollHeight -
            mobileChatContainerRef?.current?.clientHeight -
            10
        ) {
          setPositionScroll("bottom")
        } else {
          setPositionScroll("middle")
        }
      }

      handleDisableControls()
    }

    const [firstOpen, setFirstOpen] = React.useState(true)

    React.useEffect(() => {
      if (targetTabIndex === 2) {
        setFirstOpen(true)
      }

      if (targetTabIndex === 0) {
        mobileChatContainerRef.current.scrollTop =
          mobileChatContainerRef.current.scrollHeight

        let timer = setTimeout(() => {
          setFirstOpen(false)
        }, 1000)
        return () => clearTimeout(timer)
      }
    }, [targetTabIndex, isLandscape])

    return (
      <Box
        position={"unset"}
        overflowY={isFullScreen ? "inherit" : "auto"}
        maxW={
          isLandscape
            ? "calc(100% - 11rem)"
            : isTransparent && `calc(100% - 12px - ${paddingLeft})`
        }
        id="messages-container-mobile"
        maxH={
          !isFullScreenChat
            ? "100%"
            : isLandscape
            ? `${185 - 45}px`
            : screenSize.height * 0.46 - 51
        }
        d="flex"
        zIndex={isFullScreenChat && VIDEO_OVERLAY_Z_INDEX}
        pos={isFullScreenChat ? "absolute" : "unset"}
        marginTop="auto"
        width="100%"
        bottom={isFullScreenChat && mobileFullScreenChatPosition}
        marginLeft={isFullScreenChat && "10px"}
        flexDirection="column"
        style={{ scrollBehavior: "smooth" }}
        paddingBottom={`calc(0.5rem - 1px + ${paddingBottom})`}
        // onScroll={handleOnScroll}
      >
        {(isTransparent || isLandscape) && 
          <Box position={"relative"} width="36px" paddingBottom={"6px"}>
            <CloseTabsIcon position="unset" />
          </Box>
        }
         
        {!isLandscape && isTransparent && (
          <ChatPolls
            isMobile={true}
            isFullScreen={isFullScreen}
            isLandscape={isLandscape}
            isTransparent={isTransparent}
          />
        )}
        {bottomMostUnread && !isFullScreen && (
          <Flex
            cursor="pointer"
            zIndex={1000000000}
            onClick={handleGoDown}
            left={"47%"}
            bg={roomStyles?.secondaryAccessButtonBackgroundColor ?? "gray.400"}
            pos="absolute"
            borderRadius="50%"
            justifyContent="center"
            alignContent="center"
            opacity="0.75"
            bottom={"10px"}
            transform={"unset"}
          >
            <Icon
              src={VscArrowDown}
              fontSize={"2rem"}
              padding="3px"
              color={roomStyles?.secondaryAccessButtonTextColor ?? "white"}
            />
          </Flex>
        )}

        {topMostUnread && !isFullScreen && (
          <Flex
            cursor="pointer"
            zIndex={1000000000}
            onClick={handleGoUp}
            left={"47%"}
            bg={roomStyles?.secondaryAccessButtonBackgroundColor ?? "gray.400"}
            pos="absolute"
            borderRadius="50%"
            justifyContent="center"
            alignContent="center"
            opacity="0.75"
            top="10px"
            transform={"rotate(180deg)"}
          >
            <Icon
              src={VscArrowDown}
              fontSize={"2rem"}
              padding="3px"
              color={roomStyles?.secondaryAccessButtonTextColor ?? "white"}
            />
          </Flex>
        )}

        <Container
          display="flex"
          style={firstOpen ? {} : { scrollBehavior: "smooth" }}
          flexDirection="column"
          width={isFullScreenChat && "100%"}
          marginTop="auto"
          overflow="auto"
          positionscroll={positionScroll}
          id={ElementId.MOBILE_CHAT_CONTAINER}
          ref={mobileChatContainerRef}
          className={isTransparent && "hide-scrollbar"}
        >
          {allChatMessages.length === 0 && (
            <FadeChat isSmallView={true} fadingDirections={"top"} />
          )}
          {allChatMessages.length !== 0 && (
            <ChatVirtualizedList
              list={allChatMessages}
              renderItem={renderChatMessage}
              isMobile={isMobile}
              scrollElementId={ElementId.MOBILE_CHAT_CONTAINER}
              onScrollCallback={handleScroll}
              renderDaySplit={date => (
                <DaySplitter
                  styleschat={styleschat}
                  date={date}
                  isMobile={true}
                  isTransparent={isTransparent}
                />
              )}
            />
          )}
        </Container>
      </Box>
    )
  },
  {
    fallback: <ComponentFallback />
  }
)

export const MobileChatStream = React.memo(_MobileChatStream)
